import type { ModelInfo } from '@paradigm/terminal-core';

import { defaultModelLabel } from './model-labels';

/**
 * Native offline fallback for the model picker, shown before (or when) the live
 * `/api/models` fetch resolves. It lives IN the package — not injected by the
 * host — because the catalog is a property of the *gateway*, not of the app
 * embedding the terminal: every host that pairs with a given gateway should see
 * the same list. Keyed by gateway host suffix because Bedrock ids are
 * region-pinned (`jp.*` vs `eu.*`) and the gateway rejects ids from the wrong
 * region, so the fallback must match the cluster the URL points at.
 *
 * Entries carry only `{ id, legacy? }`; friendly names come from
 * `defaultModelLabel` (the single source of labels, shared with the live path).
 * The live catalog replaces this list once it loads, so the table only needs
 * sensible defaults (the Anthropic tier + the headline non-Anthropic models),
 * not the gateway's full inventory.
 */
interface StaticModel {
  readonly id: string;
  readonly legacy?: boolean;
}

// Mirrors each cluster's BEDROCK_MODEL_IDS (infra/helm/terminal). Opus stays
// first so the picker's Opus-first default resolves without a live fetch.
const MODELS_BY_HOST_SUFFIX: Readonly<Record<string, readonly StaticModel[]>> =
  {
    // infra/helm/terminal/environments/terminal-prod.yaml (jp region)
    'terminal.dime.gg': [
      { id: 'jp.anthropic.claude-opus-4-8' },
      { id: 'jp.anthropic.claude-fable-5' },
      { id: 'jp.anthropic.claude-opus-4-7', legacy: true },
      { id: 'jp.anthropic.claude-sonnet-4-6' },
      { id: 'jp.anthropic.claude-haiku-4-5-20251001-v1:0' },
      { id: 'deepseek.v3.2' },
      { id: 'google.gemma-3-27b-it' },
      { id: 'zai.glm-5' },
      { id: 'qwen.qwen3-235b-a22b-2507-v1:0' },
      { id: 'openai.gpt-oss-120b-1:0' },
      { id: 'mistral.mistral-large-3-675b-instruct' },
    ],
    // infra/helm/terminal/values.yaml chart default (eu region)
    'terminal.paradex.tech': [
      { id: 'eu.anthropic.claude-sonnet-4-6' },
      { id: 'eu.anthropic.claude-haiku-4-5-20251001-v1:0' },
      { id: 'eu.anthropic.claude-opus-4-7', legacy: true },
    ],
    'terminal.paradigm.tech': [
      { id: 'eu.anthropic.claude-sonnet-4-6' },
      { id: 'eu.anthropic.claude-haiku-4-5-20251001-v1:0' },
      { id: 'eu.anthropic.claude-opus-4-7', legacy: true },
    ],
  };

// Unknown gateways (custom Lightsail, dev forks): the `eu.*` chart default,
// which the live fetch corrects if the pod is region-pinned elsewhere.
const DEFAULT_MODELS: readonly StaticModel[] = [
  { id: 'eu.anthropic.claude-sonnet-4-6' },
  { id: 'eu.anthropic.claude-haiku-4-5-20251001-v1:0' },
  { id: 'eu.anthropic.claude-opus-4-7', legacy: true },
];

function toModelInfo(m: StaticModel): ModelInfo {
  const label = defaultModelLabel(m.id);
  return m.legacy === true
    ? { id: m.id, label, legacy: true }
    : { id: m.id, label };
}

/**
 * Offline fallback list for the gateway at `agentUrl`. Returns `[]` for an
 * unparseable/empty URL so the picker simply stays hidden until the live fetch
 * lands, rather than guessing a wrong-region list.
 */
export function getStaticModels(agentUrl: string | null): readonly ModelInfo[] {
  if (agentUrl == null || agentUrl.length === 0) return [];
  let host: string;
  try {
    host = new URL(agentUrl).hostname;
  } catch {
    return [];
  }
  for (const [suffix, models] of Object.entries(MODELS_BY_HOST_SUFFIX)) {
    // Anchored suffix match — `evilterminal.dime.gg` must not match
    // `terminal.dime.gg`; only exact or proper-subdomain matches.
    if (host === suffix || host.endsWith(`.${suffix}`)) {
      return models.map(toModelInfo);
    }
  }
  return DEFAULT_MODELS.map(toModelInfo);
}
